'use client';

import {
  BodyDefault,
  Button,
  Drawer,
  EmptyState,
  Filter,
  HeadingItem,
  IconButton,
  Spinner,
  VirtualizedStack,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  WorkHistoryIcon,
  Search01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  type HistoryFilters,
  useCrawlerHistory,
} from '../../hooks/useCrawlerSessions';
import { useApplicationCredentialsList } from '../../hooks/api/useApplicationCredentialsList';
import { useCrawlerUnseenStore } from '../../store/crawlerUnseenStore';
import { CrawlerHistorySessionRow } from './CrawlerHistorySessionRow';
import {
  type CrawlerHistoryTriggerFilter,
  formatDayGroupLabel,
  groupSessionsByDay,
  toHistoryApiFilters,
  type HistorySessionWithMeta,
} from './crawlerHistory.utils';

const DRAWER_WIDTH = '360px';

type CrawlerHistoryDrawerProps = {
  applicationId: string;
  isOpen: boolean;
  onClose: () => void;
};

export function CrawlerHistoryDrawer({
  applicationId,
  isOpen,
  onClose,
}: CrawlerHistoryDrawerProps) {
  const t = useTranslations('crawler.historyDrawer');
  const filterIdPrefix = useId();
  const clearHistoryUnseen = useCrawlerUnseenStore(
    (state) => state.clearHistoryUnseen,
  );
  const [credentialFilterValues, setCredentialFilterValues] = useState<
    string[]
  >([]);
  const [triggerFilterValues, setTriggerFilterValues] = useState<string[]>([]);
  const [daysFilterValues, setDaysFilterValues] = useState<string[]>([]);
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const triggerFilter: CrawlerHistoryTriggerFilter =
    triggerFilterValues[0] === 'MANUAL' || triggerFilterValues[0] === 'CRAWLER'
      ? triggerFilterValues[0]
      : 'all';
  const daysFilter = daysFilterValues[0];
  const filters = useMemo((): HistoryFilters => {
    const credentialIds = credentialFilterValues.map((value) =>
      value === 'anonymous' ? null : value,
    );
    let fromDate: string | undefined;

    if (daysFilter && daysFilter !== 'all') {
      const days = Number.parseInt(daysFilter, 10);

      if (!Number.isNaN(days)) {
        const from = new Date();

        from.setDate(from.getDate() - days);
        fromDate = from.toISOString();
      }
    }

    return toHistoryApiFilters({
      trigger: triggerFilter,
      credentialIds,
      fromDate,
    });
  }, [credentialFilterValues, triggerFilter, daysFilter]);
  const hasActiveFilters =
    credentialFilterValues.length > 0 ||
    triggerFilterValues.length > 0 ||
    daysFilterValues.length > 0;
  const { data: credentialsData } = useApplicationCredentialsList({
    applicationId,
    enabled: isOpen,
  });
  const credentialMap = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        username?: string;
        role?: string;
      }
    >();

    credentialsData?.credentials?.forEach((cred) => {
      map.set(cred.id, cred);
    });

    return map;
  }, [credentialsData?.credentials]);
  const credentialFilterItems = useMemo(
    () => [
      ...(credentialsData?.credentials ?? []).map((credential) => ({
        value: credential.id,
        label: credential.role
          ? `${credential.username || credential.id} (${credential.role})`
          : credential.username || credential.id,
      })),
      { value: 'anonymous', label: t('anonymous') },
    ],
    [credentialsData?.credentials, t],
  );
  const triggerFilterItems = useMemo(
    () => [
      { value: 'MANUAL', label: t('triggerManual') },
      { value: 'CRAWLER', label: t('triggerCrawler') },
    ],
    [t],
  );
  const daysFilterItems = useMemo(
    () => [
      { value: '1', label: t('date1Day') },
      { value: '7', label: t('date7Days') },
      { value: '30', label: t('date30Days') },
      { value: 'all', label: t('dateAllTime') },
    ],
    [t],
  );
  const { sessions, isLoading, hasMore, loadMore } = useCrawlerHistory({
    applicationId,
    filters,
    enabled: isOpen,
  });
  const showFilters = sessions.length > 0 || hasActiveFilters;
  const enrichedSessions = useMemo((): HistorySessionWithMeta[] => {
    return sessions.map((session) => {
      const credInfo = session.credentialId
        ? credentialMap.get(session.credentialId)
        : null;
      let credentialName = t('anonymous');
      let credentialRole: string | undefined;

      if (credInfo) {
        credentialName = credInfo.username || `${credInfo.id.slice(0, 8)}...`;
        credentialRole = credInfo.role;
      } else if (session.credentialId) {
        credentialName = `${session.credentialId.slice(0, 8)}...`;
      }

      return {
        ...session,
        credentialName,
        credentialRole,
      };
    });
  }, [sessions, credentialMap, t]);
  const groupedSessions = useMemo(
    () => groupSessionsByDay(enrichedSessions),
    [enrichedSessions],
  );
  const flatRows = useMemo(() => {
    return groupedSessions.flatMap((group) => {
      const isCollapsed = collapsedDays.has(group.dayKey);
      const dayLabelRaw = formatDayGroupLabel(group.labelDate);
      const dayLabel =
        dayLabelRaw === 'today'
          ? t('today')
          : dayLabelRaw === 'yesterday'
            ? t('yesterday')
            : dayLabelRaw;

      return [
        {
          kind: 'day' as const,
          id: `day-${group.dayKey}`,
          dayKey: group.dayKey,
          label: dayLabel,
          isCollapsed,
        },
        ...(isCollapsed
          ? []
          : group.sessions.map((session) => ({
              kind: 'session' as const,
              id: session.id,
              session,
            }))),
      ];
    });
  }, [groupedSessions, collapsedDays, t]);
  const resetFilters = () => {
    setCredentialFilterValues([]);
    setTriggerFilterValues([]);
    setDaysFilterValues([]);
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      clearHistoryUnseen(applicationId);
    }
  }, [isOpen, applicationId, clearHistoryUnseen]);
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen]);

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      lazyMount
      unmountOnExit
    >
      <Drawer.Backdrop />
      <Drawer.Positioner w={DRAWER_WIDTH}>
        <Drawer.Content
          display="flex"
          flexDirection="column"
          overflowX="hidden"
          h="full"
        >
          <Drawer.Header px="3" py="3">
            <HStack justifyContent="space-between" w="full" alignItems="center">
              <HeadingItem>{t('title')}</HeadingItem>
              <Drawer.CloseTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={t('closeAriaLabel')}
                >
                  <Cancel01Icon size={16} />
                </IconButton>
              </Drawer.CloseTrigger>
            </HStack>
          </Drawer.Header>

          <Drawer.Body
            display="flex"
            flexDirection="column"
            flex="1"
            minH="0"
            overflow="hidden"
            p="0"
          >
            {showFilters ? (
              <HStack px="3" py="3" gap="2" flexWrap="wrap" alignItems="center">
                <Filter
                  id={`${filterIdPrefix}-credential`}
                  density="compact"
                  isMultiSelect
                  placeholder={t('credentialFilter')}
                  items={credentialFilterItems}
                  value={credentialFilterValues}
                  onChange={setCredentialFilterValues}
                  contentWidth="240px"
                />
                <Filter
                  id={`${filterIdPrefix}-trigger`}
                  density="compact"
                  placeholder={t('triggerFilter')}
                  items={triggerFilterItems}
                  value={triggerFilterValues}
                  onChange={setTriggerFilterValues}
                  contentWidth="160px"
                />
                <Filter
                  id={`${filterIdPrefix}-date`}
                  density="compact"
                  placeholder={t('dateFilter')}
                  items={daysFilterItems}
                  value={daysFilterValues}
                  onChange={setDaysFilterValues}
                  contentWidth="160px"
                />
              </HStack>
            ) : null}

            <Box flex="1" overflow="hidden" minH="0" h="full" px="1">
              {isLoading && sessions.length === 0 ? (
                <Box display="flex" justifyContent="center" py="8">
                  <Spinner size="sm" />
                </Box>
              ) : sessions.length === 0 && !hasActiveFilters ? (
                <EmptyState
                  title={t('noCrawlsYet')}
                  icon={<WorkHistoryIcon size={24} />}
                  maxWidth="full"
                />
              ) : sessions.length === 0 ? (
                <EmptyState
                  title={t('noCrawling')}
                  description={t('noMatchesDescription')}
                  icon={<Search01Icon size={24} />}
                  maxWidth="full"
                >
                  <Button size="xs" variant="outline" onClick={resetFilters}>
                    {t('clearFilters')}
                  </Button>
                </EmptyState>
              ) : (
                <VirtualizedStack
                  data={flatRows}
                  computeItemKey={({ item }) => item.id}
                  endReached={() => {
                    if (hasMore) {
                      void loadMore();
                    }
                  }}
                  renderItem={({ item }) => {
                    if (item.kind === 'day') {
                      const collapseLabel = item.isCollapsed
                        ? t('expandDayAriaLabel')
                        : t('collapseDayAriaLabel');

                      return (
                        <Button
                          variant="ghost"
                          size="xs"
                          w="full"
                          justifyContent="flex-start"
                          gap="1"
                          px="2"
                          py="2"
                          mt="1"
                          aria-label={collapseLabel}
                          aria-expanded={!item.isCollapsed}
                          onClick={() => {
                            setCollapsedDays((prev) => {
                              const next = new Set(prev);

                              if (next.has(item.dayKey)) {
                                next.delete(item.dayKey);
                              } else {
                                next.add(item.dayKey);
                              }

                              return next;
                            });
                          }}
                        >
                          {item.isCollapsed ? (
                            <ArrowRight01Icon size={14} />
                          ) : (
                            <ArrowDown01Icon size={14} />
                          )}
                          <BodyDefault>{item.label}</BodyDefault>
                        </Button>
                      );
                    }

                    return (
                      <CrawlerHistorySessionRow
                        session={item.session}
                        nowMs={nowMs}
                      />
                    );
                  }}
                />
              )}
            </Box>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
